<script>
import axios from "axios";
import API from "@/services/API.js";
import Utils from "@/services/utils.js";
import MonitoringDisplay from "@/components/monitoring/Display";
import Titlebar from "@/components/ui/Titlebar";
import UiCard from "@/components/ui/Card";
import Toolbar from "@/components/ui/Toolbar";
import UiButton2 from '@/components/ui/Button2';
import UiSpinner from "@/components/ui/UiSpinner";
import UiRelativeDate from "@/components/ui/RelativeDate";
import { UiTabs, UiTab } from '@/components/ui/ui-tabs';
import {useDvFlagAccess} from "../../services/featureFlag";

export default {
  name: "RemoteMonitoringShow",
  components: {
    MonitoringDisplay,
    Titlebar,
    Toolbar,
    UiButton2,
    UiCard,
    UiSpinner,
    UiTab,
    UiTabs,
    UiRelativeDate, 
  },
  data() {
    return {
      viewState: 'INITIAL', // INITIAL, LOADING, ERROR, SUCCESS
      rmData: null,
    };
  },
  mounted() {
    if(!useDvFlagAccess('beepiz-remote-monitoring-can:read')){
      this.$router.push({ name: 'AccessDenied', params: { slug: "remotemonitoring_no_role"} })
    }
  },
  methods: {
    getRm(id) {
      this.viewState = 'LOADING'
      axios
        .get(API.route(API.endpoints.remoteMonitoring.show, { procedureId: id }))
        .then((response) => {
          this.rmData = response.data;
          this.viewState = 'SUCCESS';
        })
        .catch((error) => {
          this.viewState = 'ERROR';
          this.i18nErrorMessage(`remoteMonitoring.back_end_messages.${error?.response?.data?.message}`)
        });
    },
    handleEdit() {
      this.$router.push({ name: 'RemoteMonitoringForm', params: { id: this.rmData.current.id } })
    },
    handleDelete() {
      let modal_content = `<p>${Utils.t("remoteMonitoring.delete_modal_content")}</p>`;
      if (this.rmData?.current?.datis?.length > 0) {
        modal_content += `<p>${Utils.t("remoteMonitoring.delete_modal_contains_datis")}</p>`;
      }

      window.Vfc.$refs.confirm.show({
        title: Utils.t("global.deleteModalTitle"),
        content: modal_content,
        callbackYes: () => {
          this.viewState = 'LOADING';
          axios
            .delete(
              API.route(API.endpoints.remoteMonitoring.delete, {
                procedureId: this.$route.params.id,
              })
            )
            .then((response) => {
              window.Vfc.$refs.messages.sendMessage(
                "success",
                Utils.t("remoteMonitoring.procedures.deleteSuccess")
              );
              this.$router.push({ name: "RemoteMonitoringList" });
            })
            .catch((error) => {
              this.viewState = 'ERROR';
              this.i18nErrorMessage(`remoteMonitoring.back_end_messages.${error?.response?.data?.message}`)
            });
        },
        yesButtonText: Utils.tc("global.delete"),
        noButtonText: Utils.tc("global.cancel"),
      });
    },
    i18nErrorMessage(i18nErrorPath) {
      window.Vfc.$refs.messages.sendMessage(
        'error',
        this.$te(i18nErrorPath)
          ? this.$t(i18nErrorPath)
          : this.$t('errors.default')
      )
    },
  },
  created() {
    if (this.$route.params?.id) {
      this.getRm(this.$route.params.id);
    } else {
      console.error("No remote Id provided", this.$route);
    }
    window.Vfc.$refs.loading.hide();
  },
  computed: {
    canBrowse() {
      return useDvFlagAccess("beepiz-remote-monitoring-can:read");
    },
    infoMessage() {
      if (this.rmData?.current?.status === 'verified') {
        return {
          className: 'alert-success',
          title: this.$t("remoteMonitoring.display.info_verified.title"),
          body: this.$t("remoteMonitoring.display.info_verified.body"),
        }
      }
      if (this.rmData?.current?.status === 'waiting') {
        return {
          className: 'alert-info',
          title: this.$t("remoteMonitoring.display.info_waiting.title"),
          body: this.$t("remoteMonitoring.display.info_waiting.body"),
        }
      }
      if (this.rmData?.current?.status === 'refused') {
        return {
          className: 'alert-warning',
          title: this.$t("remoteMonitoring.display.info_incomplete.title"),
          body: this.$t("remoteMonitoring.display.info_incomplete.body"),
        }
      }
      return null
    }
  },
};

</script>

<template>
  <div class="page-content pb-0">
    <Titlebar
      incrustation="fa-bullhorn"
      :backPath="{ name: 'RemoteMonitoringList'}"
    >
      <h1>{{ viewState === 'SUCCESS' && !!rmData.current ? rmData.current.name : $t("remoteMonitoring.titles.manager") }}</h1>
    </Titlebar>
    <UiCard>
      <UiSpinner v-if="viewState === 'INITIAL' || viewState === 'LOADING'" />
      <section v-if="viewState === 'SUCCESS' && canBrowse">
        <div 
          v-if="infoMessage"
          class="alert text-md mb-4"
          :class="infoMessage.className"
        >
          <h5 v-html="infoMessage.title" class="font-weight-bold"></h5>
          <p class="mb-2">
            {{ $t('global.saved_last') }}&nbsp;: 
            <UiRelativeDate :date="rmData.current.updated_at.date" />
          </p>
          <p v-html="infoMessage.body" class="mb-0"></p>
        </div>
        <MonitoringDisplay 
          v-if="rmData.current.status === 'verified' || rmData.current.status === 'refused'"
          :rmData="rmData.current"
        />
        <UiTabs
          v-if="rmData.current.status === 'waiting'"
          class="full-height mt-1"
          style="display: grid; grid-template-rows: min-content 1fr;" 
          content-class="full-height overflow-auto py-4"
        >
          <UiTab :title="$t('remoteMonitoring.display.tab_current')">
            <MonitoringDisplay 
              :rmData="rmData.current"
            />
          </UiTab>
          <UiTab :title="$t('remoteMonitoring.display.tab_last')">
            <MonitoringDisplay 
              :rmData="rmData.last"
            />
          </UiTab>
        </UiTabs>
      </section>
      <p v-if="viewState === 'SUCCESS' && !canBrowse">
        {{ $t('module.forbidden') }}
      </p>
      <div class="toolbar-ghost"></div>
      <div class="d-flex align-items-center justify-content-end gap-3">
        <UiButton2 
          v-if="viewState === 'SUCCESS'"
          @click.native.stop="handleDelete()" 
          v-can="'beepiz-edit-remotemonitoring'"
          class="btn-danger btn-lg"
          :disabled="rmData.current.status === 'waiting'"
        >
          {{ $t('global.delete') }}
        </UiButton2>
        <UiButton2 
          v-if="viewState === 'SUCCESS'"
          @click.native.stop="handleEdit()" 
          v-can="'beepiz-edit-remotemonitoring'"
          class="btn-brand btn-lg"
          :disabled="rmData.current.status === 'waiting'"
        >
          {{ $t('global.edit') }}
        </UiButton2>
      </div>
    </UiCard>
  </div>
</template>

<style scoped lang="scss">
</style>